import { useInfiniteQuery } from "@tanstack/vue-query";
import { computed, toValue, type MaybeRefOrGetter } from "vue";
import { useApi } from "@shopexperts/shared";
import {
  createEmptyExpertFilters,
  expertFiltersToQuery,
  type ExpertManagerFilterState,
} from "./expertManagerFilters";

interface UseAdminExpertsOptions {
  filters?: MaybeRefOrGetter<ExpertManagerFilterState>;
  limit?: number;
}

export function useAdminExperts(options: UseAdminExpertsOptions = {}) {
  const api = useApi();
  const { filters = createEmptyExpertFilters(), limit = 20 } = options;

  const filterState = computed(() => toValue(filters));

  function listQuery(current: ExpertManagerFilterState, cursor?: string) {
    return {
      limit,
      cursor,
      search: current.search || undefined,
      status: current.status || undefined,
      expert_type: current.expertType || undefined,
      country: current.country || undefined,
      service_category: current.serviceCategory || undefined,
      partner_tier: current.partnerTier || undefined,
      language: current.language || undefined,
      is_pro: current.proStatus === "" ? undefined : current.proStatus === "true",
      claim_status: current.claimStatus || undefined,
      tag_ids: current.tagIds.length ? current.tagIds : undefined,
      exclude_tag_ids: current.excludedTagIds.length ? current.excludedTagIds : undefined,
    };
  }

  // Resolve selection separately so selecting a large view does not render every card.
  async function collectExpertIds(isCancelled: () => boolean, onProgress: (count: number) => void) {
    const snapshot = {
      ...filterState.value,
      tagIds: [...filterState.value.tagIds],
      excludedTagIds: [...filterState.value.excludedTagIds],
    };
    const ids = new Set<number>();
    const cursors = new Set<string>();
    let cursor: string | undefined;
    do {
      const page = await api.admin.expertManager.listExperts({ query: listQuery(snapshot, cursor) });
      if (isCancelled()) return null;
      page.data.forEach((expert) => ids.add(expert.id));
      onProgress(ids.size);
      if (!page.pagination.has_next) return ids;
      const next = page.pagination.cursor;
      if (!next || cursors.has(next)) throw new Error('Unable to finish selecting this view. Please retry.');
      cursors.add(next);
      cursor = next;
    } while (!isCancelled());
    return null;
  }

  const {
    data,
    isLoading,
    isFetching,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    // expertFiltersToQuery drops empty values, so the key only changes on a
    // filter that actually reaches the API.
    queryKey: computed(() => ["admin", "experts", expertFiltersToQuery(filterState.value)]),
    queryFn: async ({ pageParam }) => {
      return await api.admin.expertManager.listExperts({
        query: listQuery(filterState.value, pageParam),
      });
    },
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.has_next ? lastPage.pagination.cursor : undefined;
    },
    initialPageParam: undefined as string | undefined,
    retry: 2,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const experts = computed(() => {
    return data.value?.pages.flatMap((page) => page.data) || [];
  });

  const totalExperts = computed(() => {
    return data.value?.pages[0]?.meta?.total ?? experts.value.length;
  });

  return {
    collectExpertIds,
    experts,
    totalExperts,
    isLoading,
    isFetching,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
  };
}
